/* eslint-disable canonical/filename-match-exported -- FIXME: Fix this ESLint violation! */
import isNonNull from '@openphone/lib/isNonNull'
import { action, makeAutoObservable } from 'mobx'

import ActivityMentionAlertModel from '@src/service/model/alert/ActivityMentionAlertModel'
import ActivityReactionAlertModel from '@src/service/model/alert/ActivityReactionAlertModel'
import ContactNoteMentionAlertModel from '@src/service/model/alert/ContactNoteMentionAlertModel'
import CsvImportCompletedAlertModel from '@src/service/model/alert/CsvImportCompletedAlertModel'
import OrganizationExportCompleteAlertModel from '@src/service/model/alert/OrganizationExportCompleteAlertModel'
import PortRequestCompletedAlertModel from '@src/service/model/alert/PortRequestCompletedAlertModel'
import TaskAssignedAlertModel from '@src/service/model/alert/TaskAssignedAlertModel'
import TaskPastDueAlertModel from '@src/service/model/alert/TaskPastDueAlertModel'
import ThreadCommentReactionAlertModel from '@src/service/model/alert/ThreadCommentReactionAlertModel'
import ThreadMentionAlertModel from '@src/service/model/alert/ThreadMentionAlertModel'
import ThreadReplyAlertModel from '@src/service/model/alert/ThreadReplyAlertModel'
import ThreadResolutionAlertModel from '@src/service/model/alert/ThreadResolutionAlertModel'

import type Service from '.'
import Collection from './collections/Collection'
import type { CodableConversation, IActivity } from './model'
import type AlertAssociations from './model/alert/AlertAssociations'
import type AlertType from './model/alert/AlertType'
import type CodableAlert from './model/alert/CodableAlert'
import type DecodableAlert from './model/alert/DecodableAlert'
import type { CodableContact } from './model/contact/ContactModel'
import type { AlertDeleteMessage, AlertUpdateMessage } from './transport/websocket'

export default class AlertStore {
  readonly collection = new Collection<AlertType>()

  // associated pieces of data
  private readonly conversationsCollection = new Collection<CodableConversation>()
  private readonly activitiesCollection = new Collection<IActivity>()
  private readonly contactsCollection = new Collection<CodableContact>()

  constructor(private root: Service) {
    makeAutoObservable(this, {}, { autoBind: true })

    this.subscribeToWebhookEvents()
  }

  makeAlertModel(alert: CodableAlert) {
    switch (alert.type) {
      case 'comment-on-activity':
      case 'reply-in-mentioned-thread':
      case 'reply-in-thread':
        return new ThreadReplyAlertModel(this)
      case 'reaction-on-activity':
        return new ActivityReactionAlertModel(this)
      case 'mention-in-activity-comment':
        return new ThreadMentionAlertModel(this)
      case 'mention-in-activity':
        return new ActivityMentionAlertModel(this)
      case 'mention-in-contact-note':
        return new ContactNoteMentionAlertModel(this)
      case 'reaction-on-activity-comment':
        return new ThreadCommentReactionAlertModel(this)
      case 'activity-resolved':
      case 'activity-unresolved':
        return new ThreadResolutionAlertModel(this)
      case 'task-assigned':
        return new TaskAssignedAlertModel(this)
      case 'task-overdue':
        return new TaskPastDueAlertModel(this)
      case 'port-request-completed':
        return new PortRequestCompletedAlertModel(this)
      case 'contact-import-completed':
      case 'contact-import-errored':
        return new CsvImportCompletedAlertModel(this)
      case 'org-export-complete':
      case 'org-export-failed':
        return new OrganizationExportCompleteAlertModel()
      default:
        return null
    }
  }

  async fetch() {
    const {
      result: alerts,
      associations: { activities, contacts, conversations },
    } = await this.root.transport.account.alert.paginated()

    this.clearCollections()

    await this.store(alerts, { activities, conversations, contacts })
  }

  async markRead(alerts: AlertType[]) {
    if (alerts.length === 0) {
      return
    }
    alerts.forEach(
      action((alert) => {
        alert.readAt = Date.now()
      }),
    )
    this.collection.putBulk(alerts)
    const ids = alerts.map((a) => a.id)
    return this.root.transport.account.alert.read(ids)
  }

  async markUnread(alerts: AlertType[]) {
    if (alerts.length === 0) {
      return
    }
    alerts.forEach((alert) => {
      alert.readAt = null
    })
    this.collection.putBulk(alerts)
    const ids = alerts.map((a) => a.id)
    return this.root.transport.account.alert.unread(ids)
  }

  async markOpened(alerts: AlertType[]) {
    if (alerts.length === 0) {
      return
    }
    alerts.map(
      action((alert) => {
        alert.openedAt = Date.now()
      }),
    )
    this.collection.putBulk(alerts)
    const ids = alerts.map((a) => a.id)
    return this.root.transport.account.alert.open(ids)
  }

  async markUnopened(alerts: AlertType[]) {
    if (alerts.length === 0) {
      return
    }
    alerts.forEach((alert) => {
      alert.openedAt = null
    })
    this.collection.putBulk(alerts)
    const ids = alerts.map((a) => a.id)
    return this.root.transport.account.alert.unopen(ids)
  }

  delete(alerts: AlertType[]) {
    const ids = alerts.map((a) => a.id)
    this.collection.deleteBulk(alerts)
    return this.root.transport.account.alert.delete(ids)
  }

  getConversation(conversationId: string) {
    return this.conversationsCollection.get(conversationId)
  }

  getActivity(activityId: string) {
    return this.activitiesCollection.get(activityId)
  }

  getContact(contactId: string) {
    return this.contactsCollection.get(contactId)
  }

  getUser(userId: string) {
    return this.root.member.get(userId)
  }

  getContactImport(contactImportId: string) {
    return this.root.contact.csvImportsV2.get(contactImportId)
  }

  getPhoneNumberById(phoneNumberId: string) {
    return this.root.phoneNumber.collection.get(phoneNumberId)
  }

  getPhoneNumber(phoneNumber: string) {
    return this.root.phoneNumber.byNumber[phoneNumber]
  }

  private subscribeToWebhookEvents() {
    this.root.transport.onNotificationData.subscribe((data) => {
      switch (data.type) {
        case 'alert-update':
          return this.handleAlertUpdate(data)
        case 'alert-delete':
          return this.handleAlertDelete(data)
      }
    })
  }

  private handleAlertUpdate(message: AlertUpdateMessage) {
    void this.store([message.alert], message.associations)
  }

  private handleAlertDelete(message: AlertDeleteMessage) {
    this.collection.delete(message.alert.id)
  }

  private clearCollections() {
    this.collection.clear()
    this.conversationsCollection.clear()
    this.activitiesCollection.clear()
    this.contactsCollection.clear()
  }

  private async store(
    alerts: DecodableAlert[],
    { contacts = [], conversations = [], activities = [] }: AlertAssociations = {},
  ) {
    if (activities !== undefined) {
      this.activitiesCollection.putBulk(activities)
    }

    if (conversations !== undefined) {
      this.conversationsCollection.putBulk(conversations)
    }

    if (contacts !== undefined) {
      this.contactsCollection.putBulk(contacts)
    }

    const alertModels = alerts
      .map((alert) => this.makeAlertModel(alert)?.deserialize(alert))
      .filter(isNonNull)

    return this.collection.putBulk(alertModels)
  }
}
